import * as THREE from 'three';
import { RenderQuality } from './RenderQuality';
import { ShadowManager } from './ShadowManager';

/**
 * Owns the scene lighting rig: a directional "sun" plus hemisphere/ambient fill. In later
 * milestones the sun direction and intensities are driven by {@link EnvironmentState} via
 * `SunModel`; for now sensible daylight defaults are used.
 *
 * ## Why the fill is weaker than it looks
 *
 * `SkyEnvironment` now supplies a prefiltered sky dome as `Scene.environment`, and that dome fills
 * shade with the sky's own colour — which is the job the hemisphere and ambient lights were doing
 * approximately. Left at their old strengths on top of it, the scene came out washed out and
 * shadowless, because three fills were now stacked where the rig had been balanced for two. So the
 * hemisphere is cut to a residue and the ambient is near-zero: between them they only lift the
 * blackest shadow off the floor, and the dome does the actual filling.
 *
 * The ambient is kept rather than deleted because it is the one term the environment cannot
 * provide — `SunModel` raises it after sunset, when there is no sun and the sky dome has gone almost
 * black, and without it a night garden is unreadable.
 */
/** See {@link LightingManager.setAmbientIntensity}. */
const AMBIENT_FILL_SCALE = 0.72;

export class LightingManager {
  readonly sun: THREE.DirectionalLight;
  readonly hemisphere: THREE.HemisphereLight;
  readonly ambient: THREE.AmbientLight;
  readonly root: THREE.Group;

  private readonly shadowManager = new ShadowManager();

  constructor(quality: RenderQuality = RenderQuality.Medium) {
    this.root = new THREE.Group();
    this.root.name = 'lighting';

    this.sun = new THREE.DirectionalLight(0xfff4e0, 2.2);
    this.sun.position.set(40, 60, 30);
    this.sun.target.position.set(0, 0, 0);

    this.hemisphere = new THREE.HemisphereLight(0xbfd8ff, 0x5a4632, 0.45);
    this.ambient = new THREE.AmbientLight(0xffffff, 0.12);

    this.root.add(this.sun, this.sun.target, this.hemisphere, this.ambient);
    this.setQuality(quality);
  }

  /** Re-apply shadow configuration for a new quality preset. */
  setQuality(quality: RenderQuality): void {
    this.shadowManager.configure(this.sun, quality);
  }

  /**
   * Point the sun from a normalized direction (pointing *toward* the scene) with the given
   * intensity. Direction is where the light comes *from*.
   */
  setSunDirection(direction: THREE.Vector3, distance = 100): void {
    this.sun.position.copy(direction).normalize().multiplyScalar(distance);
    this.sun.target.position.set(0, 0, 0);
    this.sun.target.updateMatrixWorld();
  }

  setSunIntensity(intensity: number): void {
    this.sun.intensity = intensity;
  }

  /**
   * Set the ambient fill from the sun model's day/night curve.
   *
   * Scaled on the way in. `SunModel` owns the *shape* of that curve — how fill decays after sunset
   * and holds through the night — and it should go on owning it. How much fill the rig needs is a
   * different question, and its answer changed when the sky dome arrived: the dome supplies the
   * daytime fill that the ambient used to stand in for, so taking the curve at face value now
   * double-counts it at noon and flattens the scene. Trimming it puts the total back where the rig
   * was balanced. Nights come out a little darker than before, which is the correct direction — the
   * old ones were lit like an overcast afternoon.
   *
   * The scale was set by screenshotting the same frame before and after. The first attempt cut too
   * hard and left the conifers reading as near-black silhouettes, which is a worse fault than the
   * flatness it was fixing. Foliage is the sensitive surface: it carries a baked grayscale shading
   * term in its vertex colours (see `FloraMaterial`), so it darkens twice for every unit of fill
   * taken away, and it goes black long before the lawn looks dim.
   */
  setAmbientIntensity(intensity: number): void {
    this.ambient.intensity = intensity * AMBIENT_FILL_SCALE;
  }

  dispose(): void {
    this.sun.dispose();
    this.hemisphere.dispose();
    this.ambient.dispose();
  }
}
